import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX } = FiIcons;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Startseite', path: '/' },
    { name: 'Chapter', path: 'https://trustmc.eu/Chapter.html', external: true },
    { name: 'R.I.P.', path: 'https://trustmc.eu/RIP.html', external: true },
    { name: 'Kontakt', path: '/kontakt' },
    { name: 'Clubhaus', path: '/clubhaus' },
    { name: 'History', path: '/history' },
    { name: 'Partys', path: '/partys' }
  ];

  const isHome = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled || !isHome ? 'bg-black/90 backdrop-blur-xl border-b border-white/10 py-3' : 'bg-transparent py-5'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3 group">
          <motion.img 
            src="https://grafiken.trust-franken.de/HP/Kreuz.png" 
            alt="Logo" 
            className="h-12 w-auto"
            whileHover={{ rotate: 360, scale: 1.2 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          />
          <motion.span 
            className="text-xl font-black tracking-tighter hidden sm:block"
            whileHover={{ x: 5, color: '#ffffff' }}
          >
            TRUST MC FRANKEN
          </motion.span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8">
          {menuItems.map((item, idx) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              {item.external ? (
                <motion.a 
                  href={item.path} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="relative text-sm uppercase tracking-widest text-gray-400 hover:text-white transition-colors py-2 group"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" />
                </motion.a>
              ) : (
                <Link 
                  to={item.path} 
                  className={`relative text-sm uppercase tracking-widest transition-colors py-2 group ${
                    location.pathname === item.path ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <motion.span
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="block"
                  >
                    {item.name}
                  </motion.span>
                  <motion.span 
                    className={`absolute bottom-0 left-0 h-0.5 bg-white transition-all duration-300 ${
                      location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'
                    }`} 
                  />
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        {/* Mobile Toggle */}
        <motion.button 
          onClick={() => setIsMenuOpen(!isMenuOpen)} 
          className="lg:hidden text-2xl p-2"
          whileTap={{ scale: 0.8 }}
        >
          <SafeIcon icon={isMenuOpen ? FiX : FiMenu} />
        </motion.button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 top-0 left-0 w-full h-screen bg-black/95 backdrop-blur-2xl lg:hidden z-50 flex flex-col items-center justify-center space-y-8"
          >
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-8 right-8 text-4xl"
            >
              <SafeIcon icon={FiX} />
            </button>
            {menuItems.map((item, idx) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                {item.external ? (
                  <a 
                    href={item.path} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-3xl font-black uppercase tracking-tighter hover:text-gray-400"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link 
                    to={item.path} 
                    className="text-3xl font-black uppercase tracking-tighter hover:text-gray-400"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;